import React, { useState } from 'react';
import { Globe, Bot, Link2, Users } from 'lucide-react';

const features = [
  {
    icon: Globe,
    title: 'Build websites and mobile apps',
    description: 'Transform your ideas into fully functional websites and mobile apps with instant deployment, seamless data connections, and powerful scalability.',
    image: '/showcase_1.png',
  },
  {
    icon: Bot,
    title: 'Build custom agents',
    description: 'Create specialized AI agents that understand your domain. From customer support to data analysis, deploy agents that work 24/7.',
    image: '/showcase_3.png',
  },
  {
    icon: Link2,
    title: 'Build powerful integrations',
    description: 'Connect your apps to hundreds of services. Stripe, OpenAI, databases, APIs — all wired up automatically by our AI agents.',
    image: '/showcase_2.png',
  },
  {
    icon: Users,
    title: 'Build with your favourite people',
    description: 'Collaborate in real-time with team members. Share projects, review code, and deploy together from a single workspace.',
    image: '/showcase_4.png',
  },
];

const FeaturesSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div id="features" className="py-24 bg-background" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left: Feature list */}
          <div className="space-y-0">
            {features.map((feature, index) => {
              const IconComp = feature.icon;
              const isActive = index === activeIndex;
              return (
                <div
                  key={index}
                  className={`group py-7 border-b border-border first:border-t cursor-pointer -mx-4 px-4 rounded-lg transition-all ${
                    isActive ? 'bg-card' : 'hover:bg-[#0f0f0f/50]'
                  }`}
                  data-testid={`feature-item-${index}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => setActiveIndex(index)}
                >
                  <div className="flex items-start gap-4">
                    <IconComp
                      className={`w-6 h-6 mt-1 flex-shrink-0 transition-colors ${
                        isActive ? 'text-blue-400' : 'text-muted-foreground group-hover:text-foreground/80'
                      }`}
                    />
                    <div>
                      <h3
                        className={`text-xl font-semibold transition-colors ${
                          isActive ? 'text-blue-400' : 'text-white group-hover:text-foreground'
                        }`}
                      >
                        {feature.title}
                      </h3>
                      <div
                        className={`overflow-hidden transition-all duration-400 ${
                          isActive ? 'max-h-24 opacity-100 mt-2' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right: App screenshot showcase */}
          <div className="relative lg:sticky lg:top-32">
            <div className="bg-card rounded-3xl border border-border overflow-hidden shadow-2xl shadow-blue-500/5">
              <div className="relative aspect-[3/2] overflow-hidden">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-all duration-500 ${
                      index === activeIndex
                        ? 'opacity-100 scale-100'
                        : 'opacity-0 scale-[1.02]'
                    }`}
                  >
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
              {/* Bottom bar */}
              <div className="bg-muted px-6 py-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-emerald-400 font-medium">Live</span>
                    <span className="text-muted-foreground">supertoolai.app/demo</span>
                  </div>
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded bg-blue-500/20" />
                    <div className="w-6 h-6 rounded bg-purple-500/20" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturesSection;
